import { ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBizMutation, useBizQuery } from '../../Hooks/useBizQuery';
import { getProtectedSegment, unlockSegment } from '../../Services/segmentPasswords';
import { HttpError, queryClient } from '../../Services/common';
import { ModuleData } from '../../Types/containerV2';
import { useContainerContext } from './ContainerContext';
import './ProtectedSegment.scss';

export default function ProtectedSegment({ module, render }: { module: ModuleData; render: (module: ModuleData) => ReactNode }) {
	const { t, i18n } = useTranslation();
	const { containerId = 0, passwordPreview, registerSegmentContent } = useContainerContext();
	const instanceId = useId();
	const inputId = useId();
	const [password, setPassword] = useState('');
	const [message, setMessage] = useState('');
	const [expired, setExpired] = useState(false);
	const isPreview = passwordPreview !== undefined;
	// A fresh key per mount prevents previously unlocked data flashing after account/navigation changes.
	const key = ['PROTECTED_SEGMENT', containerId, module.id, i18n.language, instanceId];
	const query = useBizQuery(key, () => getProtectedSegment(containerId, module.id), !isPreview && containerId > 0, 0, 'always');
	const unlock = useBizMutation(unlockSegment, {
		onSuccessFn: (result) => {
			setPassword('');
			if (result?.unlocked) {
				setMessage('');
				void query.refetch({ cancelRefetch: false });
			} else setMessage('SEGMENT_PASSWORD.INVALID');
		},
	});
	const content =
		!query.error && !expired && query.data?.expiresUtc && new Date(query.data.expiresUtc).getTime() > Date.now()
			? query.data.module
			: null;

	useEffect(() => {
		setExpired(false);
		if (!query.data?.expiresUtc) return;
		const delay = new Date(query.data.expiresUtc).getTime() - Date.now();
		if (!Number.isFinite(delay) || delay <= 0) {
			setExpired(true);
			return;
		}
		const timer = window.setTimeout(
			() => {
				setExpired(true);
				void query.refetch({ cancelRefetch: false });
			},
			Math.max(0, delay)
		);
		return () => window.clearTimeout(timer);
	}, [query.data, query.refetch]);

	useEffect(() => {
		if (isPreview) return;
		const revalidate = () => {
			void query.refetch({ cancelRefetch: false });
		};
		window.addEventListener('focus', revalidate);
		return () => {
			window.removeEventListener('focus', revalidate);
			queryClient.removeQueries({
				queryKey: ['PROTECTED_SEGMENT', containerId, module.id, i18n.language, instanceId],
				exact: true,
			});
		};
	}, [containerId, module.id, i18n.language, instanceId, isPreview, query.refetch]);

	useEffect(() => {
		if (isPreview) return;
		registerSegmentContent(module.id, content ?? null);
		return () => registerSegmentContent(module.id, null);
	}, [module.id, content, isPreview, registerSegmentContent]);

	if (passwordPreview === 'unlocked') return render(module);
	if (!isPreview && content) return render(content);

	const busy = unlock.statusState === 'LOADING' || query.statusState === 'LOADING';
	return (
		<>
			<form
				className='biz-protected-segment'
				onSubmit={(event) => {
					event.preventDefault();
					if (isPreview || busy || !password) return;
					setMessage('');
					unlock.mutate({ containerId, moduleId: module.id, password });
				}}
			>
				<label className='biz-protected-segment__label' htmlFor={inputId}>
					{t('SEGMENT_PASSWORD.PASSWORD')}
				</label>
				<input
					id={inputId}
					type='password'
					autoComplete='current-password'
					maxLength={256}
					placeholder={t('SEGMENT_PASSWORD.PASSWORD')}
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					disabled={isPreview || busy}
					aria-describedby={message || unlock.error || query.error ? `${inputId}-error` : undefined}
				/>
				<button type='submit' disabled={isPreview || busy || !password}>
					{t('SEGMENT_PASSWORD.SHOW')}
				</button>
				{!!(message || unlock.error || query.error) && (
					<p id={`${inputId}-error`} role='alert'>
						{(message ? t(message) : null) ||
							(unlock.error instanceof HttpError && unlock.error.status === 429
								? t('SEGMENT_PASSWORD.LIMIT')
								: t('SEGMENT_PASSWORD.ERROR'))}
					</p>
				)}
			</form>
			{isPreview && <div className='biz-protected-segment-preview-content'>{render(module)}</div>}
		</>
	);
}
